class Accordion {
  // DOM要素
  el: HTMLElement | null = null
  elements: NodeListOf<Element> | null = null
  trigger: HTMLElement | null = null
  panel: HTMLElement | null = null
  single: boolean = false
  // インスタンス
  observer: MutationObserver | null = null
  abortController: AbortController | null = null
  // フラグ
  isOpen = false
  isAnimating = false

  constructor(el: HTMLElement, elements: NodeListOf<Element>) {
    if (!el) return
    // DOM要素
    this.el = el
    this.elements = elements
    this.trigger = this.el.querySelector('[role="button"][aria-expanded]') as HTMLElement
    this.panel = this.el.querySelector(`#${this.trigger.getAttribute('aria-controls')}`) as HTMLElement
    this.single = this.el.dataset.single? true : false
    this.isOpen = !this.panel.hidden
    this.abortController = new AbortController()
  }

  // 初期化
  init() {
    if (!this.panel || !this.trigger || !this.abortController) return
    this.panel.style.height = this.isOpen ? '' : '0'
    this._observeHidden()
    this.trigger.addEventListener('click', this._toggle.bind(this), { signal: this.abortController.signal })
    this.trigger.addEventListener('keydown', (e) => {
      e.key === ' ' && this._toggle(e)
    }, { signal: this.abortController.signal })
  }

  // 破棄
  destroy() {
    this.isAnimating = false
    this.isOpen = false
    this.abortController?.abort()
    this.observer?.disconnect()
    this.el = null
    this.trigger = null
    this.panel = null
    this.single = false
    this.observer = null
    this.abortController = null
  }

  // アニメーションの待機
  async _waitAnimation(target: HTMLElement) {
    const animations = target.getAnimations()
    if (animations.length === 0) {
      return Promise.resolve()
    } else {
      await Promise.allSettled(animations.map((animation) => animation.finished))
    }
  }

  // アコーディオンの開閉
  async _toggle(e: Event) {
    if (!this.trigger || !this.panel) return
    e.preventDefault()
    if (this.isAnimating) return

    this.isAnimating = true
    this.isOpen = !this.isOpen
    this.trigger.ariaExpanded = this.isOpen.toString()

    if (this.isOpen) {
      this._closeOtherPanels()
      this.panel.hidden = false
      this.panel.style.height = `${this.panel.scrollHeight}px`
      await this._waitAnimation(this.panel)
      this.panel.style.height = 'auto'
      this.isAnimating = false
    } else {
      this.panel.style.height = `${this.panel.scrollHeight}px`
      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          if (!this.panel) return
          this.panel.style.height = '0'
          await this._waitAnimation(this.panel)
          this.panel.hidden = 'until-found' as unknown as boolean
          this.isAnimating = false
        })
      })
    }
  }

  // hidden属性の監視（ページ内検索用）
  _observeHidden() {
    this.observer = new MutationObserver(() => {
      if (this.isAnimating || !this.panel || !this.trigger) return
      this.isOpen = !this.panel.hidden
      this.trigger.ariaExpanded = this.isOpen.toString()
      this.panel.style.height = this.isOpen ? 'auto' : '0'
    })

    this.observer.observe(this.panel as Node, {
      attributes: true,
      attributeFilter: ['hidden'],
    })
  }

  // 他のアコーディオンを閉じる
  _closeOtherPanels() {
    if (this.single) {
      this.elements?.forEach((element) => {
        const otherTrigger = (element as HTMLElement).querySelector('[role="button"][aria-expanded]') as HTMLElement
        const otherOpen = (element as HTMLElement).querySelector('[role="button"][aria-expanded]')?.getAttribute('aria-expanded')
        if (element !== this.el && otherOpen === 'true') {
          otherTrigger.click()
        }
      })
    }
  }
}

/**
 * アコーディオンの初期化
 */
export const accordion = {
  init: () => {
    const elements = document.querySelectorAll('.js-accordion')
    if (elements.length > 0) {
      elements.forEach((el) => {
        const accordion = new Accordion(el as HTMLElement, elements)
        accordion.init()
      })
    }
  }
}